import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import get from 'lodash/get';

import * as Fields from 'components/Fields';

import * as Api from '../../api';
import * as Mappers from '../../mappers';

interface IProps {
  name: string;
  label: string;
  applicationId: number;
  required?: boolean;
}

const ExternalFile: React.FC<IProps> = ({ name, label, applicationId, required = false, ...props }) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const value = get(values, `${name}`) || 0;

  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState({
    id: 0,
    name: '',
    extension: '',
    size: 0
  });

  useEffect(() => {
    if (file.id) {
      return;
    }

    setFile({
      id: value,
      name: value,
      extension: '',
      size: 0
    });
  }, [value, file.id]);

  const onSelect = (file: File | undefined) => {
    if (!file) {
      return;
    }

    const data = new FormData();
    data.append('file', file);

    setLoading(true);
    Api.ExternalFileUpload({ data })
      .then(response => {
        console.log(response);
        const uploadedFile = Mappers.file({
          fileId: response.data,
          name: file.name,
          size: file.size,
          extension: file.type
        });
        setFile(uploadedFile);
        setFieldValue(name, String(uploadedFile.id));
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const onView = (id: number) => {
    Api.ExternalFileDownload({ uuid: String(id) }).then(response => {
      const { headers, data } = response;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;

      let filename = file.name;

      let extension = '';

      if (headers && headers['content-type']) {
        const contentType = headers['content-type'].toLowerCase();

        // Map common MIME types to file extensions
        const mimeToExtension: { [key: string]: string } = {
          'application/pdf': '.pdf',
          'application/msword': '.doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
          'application/vnd.ms-excel': '.xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          'application/vnd.ms-powerpoint': '.ppt',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
          'text/plain': '.txt',
          'text/csv': '.csv',
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/webp': '.webp',
          'application/zip': '.zip',
          'application/x-rar-compressed': '.rar',
          'application/json': '.json',
          'application/xml': '.xml',
          'text/xml': '.xml',
          'application/octet-stream': '.bin'
        };

        // Find matching extension
        for (const [mimeType, ext] of Object.entries(mimeToExtension)) {
          if (contentType.includes(mimeType)) {
            extension = ext;
            break;
          }
        }
      }

      filename = `${filename}${extension}`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <Fields.File {...{ name, label, isLoading, onSelect, onView, file, required }} {...props} disabled={isSubmitting} />
  );
};

export default ExternalFile;
